import { useState, useEffect, useRef } from 'react';
import image1 from '../assets/big-blue-diff-assets/Hapag-LloydAG.jpg';
import image2 from '../assets/big-blue-diff-assets/India-formulating.jpg';
import image3 from '../assets/big-blue-diff-assets/Cansado.jpg';
import image4 from '../assets/big-blue-diff-assets/Business_Management.jpg';
import image5 from '../assets/big-blue-diff-assets/5.1.jpg';
import iconcard1 from '../assets/big-blue-diff-assets/icon1.png';
import iconcard2 from '../assets/big-blue-diff-assets/icon2.png';
import iconcard3 from '../assets/big-blue-diff-assets/icon3.png';
import iconcard4 from '../assets/big-blue-diff-assets/icon4.png';
import iconcard5 from '../assets/big-blue-diff-assets/icon5.png';

// TypewriterText component
const TypewriterText = ({ text, isActive, delay = 0 }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const hasCompleted = useRef(false);

  useEffect(() => {
    if (!isActive) {
      if (!hasCompleted.current) {
        setDisplayedText('');
        setCurrentIndex(0);
      }
      return;
    }

    if (!hasCompleted.current && currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(text.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, currentIndex === 0 ? delay : 90);

      return () => clearTimeout(timeout);
    }
  }, [isActive, currentIndex, text, delay]);

  useEffect(() => {
    if (currentIndex === text.length) {
      hasCompleted.current = true;
    }
  }, [currentIndex, text]);

  return (
    <span>
      {displayedText}
      {isActive && currentIndex < text.length && (
        <span className="animate-pulse">|</span>
      )}
    </span>
  );
};

const BigBlueDifference = () => {
  const headingText = "The Big Blue Difference";
  const subheadingText = "Your Supply Chain Partner Through Every Challenge";
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [currentImage, setCurrentImage] = useState(image1);
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);
  const imageRef = useRef(null);

  const cards = [
    { icon: iconcard1, title: 'Proactive Route', subtitle: 'Optimizing during port congestion' },
    { icon: iconcard2, title: 'Alternative Transport', subtitle: 'Solutions during carrier capacity issues' },
    { icon: iconcard3, title: 'Real-time', subtitle: 'Updates and transparent communication' },
    { icon: iconcard4, title: 'Contingency', subtitle: 'Planning and risk management' },
    { icon: iconcard5, title: 'Customs Expertise', subtitle: 'To navigate changing regulations' },
  ];

  const images = [image1, image2, image3, image4, image5];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (imageRef.current) {
      imageRef.current.style.opacity = '0';
      setTimeout(() => {
        setCurrentImage(hoveredIndex !== null ? images[hoveredIndex] : images[0]);
        setTimeout(() => {
          imageRef.current.style.opacity = '1';
        }, 50);
      }, 300);
    }
  }, [hoveredIndex]);

  return (
    <section ref={sectionRef} className="w-full text-[#1E3A8A] py-16">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-3xl md:text-4xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-[550] text-[#12356e]">
            {inView && (
              <TypewriterText 
                text={headingText} 
                isActive={inView} 
                delay={0} 
              />
            )}
          </h2>
          <p className="text-lg sm:text-sm md:text-base lg:text-xl xl:text-2xl 2xl:text-4xl mt-2 2xl:mt-6 font-light mb-3">
            {subheadingText}
          </p>
        </div>
        <div className="flex flex-col md:flex-row justify-center items-start gap-8 px-6">
          {/* Left side: image */}
          <div className="w-full md:w-1/2 h-[50vh] sm:h-64 md:h-[57vh] lg:h-[66vh] xl:h-[66vh] 2xl:h-[65vh]">
            <img
              ref={imageRef}
              src={currentImage}
              alt="Feature image"
              className="w-full h-full rounded-[35px] shadow-lg transition-opacity duration-300 object-cover "
            />
          </div>
          {/* Right side: cards */}
          <div 
            className="w-full md:w-1/2 flex flex-col gap-4"
            onMouseLeave={() => setHoveredIndex(null)}
          >
            {cards.map((card, index) => (
              <div
                key={index}
                className="bg-[#2ec2f0] p-5 md:p-3 lg:p-4 xl:p-4 2xl:p-5 rounded-[25px] text-white shadow-xl flex items-center cursor-pointer hover:bg-[#3ab8e6] transition-colors"
                style={{
                  boxShadow: 'inset 0 14px 8px rgba(0,0,0,0.1), inset 0 -20px 8px rgba(255,255,255,0.25)',
                  backgroundImage: 'linear-gradient(to bottom right, #45cbee, #2ec2f0)'
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onClick={() => setHoveredIndex(index)}
              >
                <img src={card.icon} alt={card.title} className="w-12 h-12 mr-4" />
                <div>
                  <h3 className="text-base sm:text-xl lg:text-3xl 2xl:text-4xl font-semibold">{card.title}</h3>
                  <p className="text-xs sm:text-sm lg:text-lg 2xl:text-2xl">{card.subtitle}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BigBlueDifference;